// Imperative confirm() replacement — use anywhere without lifting state.
//
// Setup: mount <ConfirmHost /> once in the root layout (already done in main.tsx).
// Usage:
//   const ok = await confirm({ title: 'Xoá?', description: '…', tone: 'danger' })
//   if (ok) doDelete()
//
// Similarly `notify({title, description})` for informational modals (replaces alert()).

import * as React from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './alert-dialog'
import { cn } from '@/lib/utils'
import { buttonVariants } from './button'

interface ConfirmOptions {
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
}

interface NotifyOptions {
  title: string
  description?: React.ReactNode
  actionLabel?: string
}

type Kind =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'notify'; opts: NotifyOptions; resolve: () => void }

// Module-level bus — set by ConfirmHost on mount.
let bus: ((entry: Kind) => void) | null = null

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (!bus) return Promise.resolve(window.confirm(opts.title))
  return new Promise((resolve) => bus!({ kind: 'confirm', opts, resolve }))
}

export function notify(opts: NotifyOptions): Promise<void> {
  if (!bus) { window.alert(opts.title); return Promise.resolve() }
  return new Promise((resolve) => bus!({ kind: 'notify', opts, resolve }))
}

export function ConfirmHost() {
  const [entry, setEntry] = React.useState<Kind | null>(null)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    bus = (e) => { setEntry(e); setOpen(true) }
    return () => { bus = null }
  }, [])

  function close(result?: boolean) {
    setOpen(false)
    if (!entry) return
    if (entry.kind === 'confirm') entry.resolve(result ?? false)
    else entry.resolve()
    // Delay clear so exit animation runs
    setTimeout(() => setEntry(null), 150)
  }

  if (!entry) return null

  const isDanger = entry.kind === 'confirm' && entry.opts.tone === 'danger'
  const title = entry.opts.title
  const description = entry.opts.description
  const confirmLabel =
    entry.kind === 'confirm' ? entry.opts.confirmLabel ?? 'Xác nhận'
    : entry.opts.actionLabel ?? 'Đã hiểu'
  const cancelLabel = entry.kind === 'confirm' ? entry.opts.cancelLabel ?? 'Hủy' : null

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && close(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {cancelLabel && (
            <AlertDialogCancel onClick={() => close(false)}>{cancelLabel}</AlertDialogCancel>
          )}
          <AlertDialogAction
            onClick={() => close(true)}
            className={cn(isDanger && buttonVariants({ variant: 'destructive' }))}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
